import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { showErrorNotification, showSuccessNotification } from '@/components/common/utils/notify';

export interface BranchDTO {
    branch: string;
    brFname?: string;
    brName?: string;
    brsesStat?: string;
    brsesDate?: string;
    address?: string;
    telephone?: string;
    mobile?: string;
    email?: string;
    website?: string;
    logo?: string;
    contactPerson?: string;
    regNo?: string;
    region?: string;
    district?: string;
    ward?: string;
    postalAddress?: string;
    managerName?: string;
    openingHours?: string;
    servicesOffered?: string;
    gpsCoordinates?: string;
}

const BASE = '/api/admin/branches';

async function apiFetch<T>(url: string, options?: RequestInit): Promise<T> {
    const res = await fetch(url, {
        headers: { 'Content-Type': 'application/json' },
        ...options,
    });
    if (!res.ok) {
        const text = await res.text().catch(() => res.statusText);
        throw new Error(text || res.statusText);
    }
    return res.json();
}

export const useBranches = () =>
    useQuery<BranchDTO[]>({
        queryKey: ['branches'],
        queryFn: () => apiFetch(BASE),
    });

export const useBranch = (code: string | undefined) =>
    useQuery<BranchDTO>({
        queryKey: ['branches', code],
        queryFn: () => apiFetch(`${BASE}/${code}`),
        enabled: !!code,
    });

export const useCreateBranch = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: BranchDTO) =>
            apiFetch<BranchDTO>(BASE, { method: 'POST', body: JSON.stringify(dto) }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['branches'] });
            showSuccessNotification('Branch created successfully');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};

export const useUpdateBranch = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (dto: BranchDTO) =>
            apiFetch<BranchDTO>(`${BASE}/${dto.branch}`, {
                method: 'PUT',
                body: JSON.stringify(dto),
            }),
        onSuccess: (_, vars) => {
            qc.invalidateQueries({ queryKey: ['branches'] });
            qc.invalidateQueries({ queryKey: ['branches', vars.branch] });
            showSuccessNotification('Branch updated successfully');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};

export const useActivateBranch = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (code: string) =>
            apiFetch<BranchDTO>(`${BASE}/${code}/activate`, { method: 'PUT' }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['branches'] });
            showSuccessNotification('Branch activated');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};

export const useDeactivateBranch = () => {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (code: string) =>
            apiFetch<BranchDTO>(`${BASE}/${code}/deactivate`, { method: 'PUT' }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ['branches'] });
            showSuccessNotification('Branch deactivated');
        },
        onError: (e: Error) => showErrorNotification(e.message),
    });
};
